'use client';

import { usePathname } from 'next/navigation';
import {
  describeOpenDays,
  formatDayHours,
  formatTime12,
  openState,
  summariseWeek,
  timeInWords,
} from '@/lib/hours';
import { HOURS_EXCEPTIONS, WEEK_HOURS } from '@/lib/data/hours';
import { SHOP } from '@/lib/data/shop';
import { isTrading, openingLine } from '@/lib/opening';
import { useLondonNow } from './useLondonNow';

function describeNow(now: Date): string {
  const state = openState(now, WEEK_HOURS, HOURS_EXCEPTIONS);
  if (state.note && !state.open) return state.note;
  if (state.open) {
    if (state.minutesUntilClose !== null && state.minutesUntilClose <= 60) {
      return `Closes in ${state.minutesUntilClose} min`;
    }
    // closesAt is the session actually running, which past midnight is
    // yesterday's, not today's row.
    return state.closesAt ? `Open till ${formatTime12(state.closesAt)}` : 'Open now';
  }
  if (state.nextOpen)
    return `Closed, opens ${formatTime12(state.nextOpen.time)} ${state.nextOpen.day}`;
  return 'Closed';
}

/**
 * The time the coals go on for the session that matters right now: the one
 * running (yesterday's, past midnight), today's if it is yet to open, otherwise
 * the next day the shop trades.
 *
 * Before the clock has been read only a time true every trading day can be
 * stated, so the static render uses the weekly rows and ignores exceptions,
 * the same compromise summariseWeek makes for the status line beneath. It is
 * corrected the moment the clock ticks.
 */
export function lightingTime(now: Date | null): string | null {
  if (!now) {
    const first = WEEK_HOURS.find((day) => day !== null);
    if (!first) return null;
    const uniform = WEEK_HOURS.every((day) => day === null || day.open === first.open);
    return uniform ? first.open : null;
  }
  const state = openState(now, WEEK_HOURS, HOURS_EXCEPTIONS);
  if (state.open) return state.openedAt;
  if (state.nextOpen) return state.nextOpen.time;
  return state.todayHours?.open ?? null;
}

/**
 * The clock has to be read in the browser: this page is static and cached, so a
 * status baked in at build time would be wrong within the hour. Until the shared
 * clock has ticked, the static render states the plain opening hours, which is
 * true whenever it is served.
 *
 * Two lines: the lighting time as display type ("Lit at four,") and the live
 * state beneath it in the label face, between two short rules.
 */
/**
 * On find-us the top line is today's row rather than the lighting time: the
 * hours table is on that page, so the nav points at it. Before the clock is
 * read, the row can be stated only if every trading day is the same.
 */
export function todayLine(now: Date | null): string | null {
  if (!now) {
    const first = WEEK_HOURS.find((day) => day !== null);
    const uniform = WEEK_HOURS.every(
      (day) => day === null || (first && day.open === first.open && day.close === first.close),
    );
    return uniform && first ? `Today, ${formatDayHours(first)}` : null;
  }
  const state = openState(now, WEEK_HOURS, HOURS_EXCEPTIONS);
  return `Today, ${formatDayHours(state.todayHours).toLowerCase()}`;
}

/**
 * The status line under today's row. "Open till midnight" would repeat the
 * row, so while open it says so plainly, or counts down the last hour.
 */
function describeUnderToday(now: Date): string {
  const state = openState(now, WEEK_HOURS, HOURS_EXCEPTIONS);
  if (state.note && !state.open) return state.note;
  if (state.open) {
    if (state.minutesUntilClose !== null && state.minutesUntilClose <= 60) {
      return `Closes in ${state.minutesUntilClose} min`;
    }
    return 'Open now';
  }
  if (state.nextOpen)
    return `Closed, opens ${formatTime12(state.nextOpen.time)} ${state.nextOpen.day}`;
  return 'Closed';
}

export function OpenStatus({ className = '' }: { className?: string }) {
  const now = useLondonNow();
  const pathname = usePathname();
  const onFindUs = pathname?.startsWith('/find-us') ?? false;
  // Before opening day the nav says so, on every page, and nothing else.
  const trading = now ? isTrading(now, SHOP) : SHOP.trading;
  if (!trading) {
    return (
      <div className={`flex flex-col items-center text-center ${className}`}>
        <p className="font-display text-[26px] leading-none tracking-[-0.015em] text-brick md:text-[34px]">
          {openingLine(SHOP.openingDate)}
        </p>
        <p className="label-xs mt-3 inline-flex items-center gap-3 text-ink-soft">
          <span aria-hidden="true" className="h-px w-6 bg-brick" />
          Not open yet
          <span aria-hidden="true" className="h-px w-6 bg-brick" />
        </p>
      </div>
    );
  }
  const lit = lightingTime(now);
  const top = onFindUs ? todayLine(now) : lit ? `Lit at ${timeInWords(lit)},` : null;
  const text = onFindUs
    ? now
      ? describeUnderToday(now)
      : describeOpenDays(WEEK_HOURS)
    : now
      ? describeNow(now)
      : summariseWeek(WEEK_HOURS);

  return (
    <div className={`flex flex-col items-center text-center ${className}`}>
      <p className="font-display text-[26px] leading-none tracking-[-0.015em] text-ink md:text-[34px]">
        {top ?? '\u00a0'}
      </p>
      <p className="label-xs mt-3 inline-flex items-center gap-3 text-ink-soft">
        <span aria-hidden="true" className="h-px w-6 bg-brick" />
        {text}
        <span aria-hidden="true" className="h-px w-6 bg-brick" />
      </p>
    </div>
  );
}
